import type {
  UseMutationOptions,
  UseQueryOptions,
} from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import {
  apiRequest,
  isApiError,
  type ApiError,
  useApiMutation,
  useApiQuery,
} from "@/shared/api";
import type { ProfileResponse } from "../interfaces/profile.interface";
import { clearStoredAccessToken } from "@/shared/utils/auth";
import { clearMembershipSelection } from "@/shared/utils/membership";
import { clearPhoneVerificationState } from "@/shared/utils/phoneVerification";
import { clearPendingEmailVerification } from "@/features/auth/utils/email-verification";

type ApiQueryOptions<TData> = Omit<
  UseQueryOptions<TData, ApiError, TData, readonly unknown[]>,
  "queryKey" | "queryFn"
>;

const profileRequest = {
  url: "/profile",
  method: "get" as const,
};

export const getProfile = () => apiRequest<ProfileResponse>(profileRequest);

export const useProfile = (options?: ApiQueryOptions<ProfileResponse>) =>
  useApiQuery<ProfileResponse, ApiError>(["profile"], profileRequest, options);

// Update Password
export type UpdatePasswordPayload = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

export type UpdatePasswordResponse = {
  message: string;
  tokenInvalidated: boolean;
};

const updatePasswordRequest = (payload: UpdatePasswordPayload) => ({
  url: "/profile/update-password",
  method: "post" as const,
  data: payload,
});

export const updatePassword = async (payload: UpdatePasswordPayload) =>
  apiRequest<UpdatePasswordResponse>(updatePasswordRequest(payload));

export const useUpdatePassword = (
  options?: UseMutationOptions<
    UpdatePasswordResponse,
    ApiError,
    UpdatePasswordPayload
  >
) => {
  const queryClient = useQueryClient();

  const cleanup = () => {
    clearStoredAccessToken();
    clearMembershipSelection();
    clearPhoneVerificationState();
    clearPendingEmailVerification();
    queryClient.clear();
  };

  return useApiMutation<UpdatePasswordResponse, UpdatePasswordPayload>(
    updatePasswordRequest,
    {
      ...options,
      onSuccess: (data, variables, context, mutation) => {
        // If token is invalidated, clean up auth state
        if (data.tokenInvalidated) {
          cleanup();
        }
        options?.onSuccess?.(data, variables, context, mutation);
      },
      onError: (error, variables, context, mutation) => {
        if (!isApiError(error)) {
          console.error("[profile] Unexpected update password error", error);
        }
        options?.onError?.(error, variables, context, mutation);
      },
    }
  );
};
